import * as React from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { Loader2Icon } from "lucide-react";

import {
  GuideExperience,
  guideSearchSchema,
} from "@/features/onboarding/GuideExperience";
import { api } from "@/trpc/react";

// Special onboarding for visitors who arrive from the LinkedIn path on the
// marketing site. Same walkthrough as /onboarding/guide, LinkedIn-focused copy.
export const Route = createFileRoute(
  "/_authenticated/(wonda)/_wonda/onboarding/guide_/linkedin",
)({
  validateSearch: zodValidator(guideSearchSchema),
  beforeLoad: async ({ context }) => {
    // Warm the welcome answers so the component can decide without a flash.
    // Unlike the base guide, we deliberately don't redirect incomplete users to
    // tools/platforms here: a fresh LinkedIn-path signup must first have the
    // "linkedin" cohort marker persisted so it survives the tools → platforms →
    // paywall bounce and funnels them back to this variant. Both the marker
    // write and the bounce happen in the component below.
    await context.trpcQueryUtils.account.getWelcomeAnswers.ensureData();
  },
  component: LinkedInGuideRoute,
});

function LinkedInGuideRoute() {
  const navigate = useNavigate();
  const { data: answers } = api.account.getWelcomeAnswers.useQuery();
  // Retry transient failures so a dropped write doesn't silently strand the
  // LinkedIn cohort on the default guide.
  const saveWelcomeAnswer = api.account.saveWelcomeAnswer.useMutation({
    retry: 2,
  });

  // Persist the LinkedIn cohort once, then route onwards. Marking before the
  // tools/platforms bounce is the whole point: the base guide (and the paywall
  // success redirect that lands on it) funnel cohort === "linkedin" back here.
  const hasRoutedRef = React.useRef(false);
  React.useEffect(() => {
    if (hasRoutedRef.current) return;
    if (!answers) return;

    // Set the one-shot guard only when we actually route (i.e. after the write
    // settles), never before: a pre-set guard would block retries if the cohort
    // write failed, dropping the user onto the default guide.
    const routeOnwards = () => {
      hasRoutedRef.current = true;
      if (!answers.tools) {
        void navigate({ to: "/onboarding/tools", replace: true });
      } else if (!answers.platforms) {
        void navigate({ to: "/onboarding/platforms", replace: true });
      }
    };

    // Marker already persisted: route onward (idempotent).
    if (answers.cohort === "linkedin") {
      routeOnwards();
      return;
    }

    // Don't fire a duplicate write while one is in flight or after its built-in
    // retries are spent (the effect re-runs as the mutation state changes).
    if (saveWelcomeAnswer.isPending || saveWelcomeAnswer.isError) return;

    // Persist the cohort BEFORE routing so it survives the tools → platforms →
    // paywall bounce. Route only on success: failed writes must not mark this
    // route as handled or send incomplete users onward without the cohort.
    saveWelcomeAnswer.mutate(
      { cohort: "linkedin" },
      { onSuccess: routeOnwards },
    );
  }, [answers, navigate, saveWelcomeAnswer]);

  // Incomplete signups are being bounced to an earlier step: show a light
  // loader instead of flashing the guide before the redirect.
  if (!answers?.tools || !answers?.platforms) {
    return (
      <div className="flex min-h-[calc(100dvh-var(--header-height,80px)-var(--footer-height,60px))] items-center justify-center">
        <Loader2Icon
          className="size-6 animate-spin"
          style={{ color: "var(--cli-text-muted)" }}
        />
      </div>
    );
  }

  return <GuideExperience variant="linkedin" />;
}
